import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../../../router/all_routes";
import {
  useGetInvoiceByIdQuery,
  useUpdateInvoiceMutation,
} from "../api/invoiceApi";
import { showToast } from "../../../../../../core/utils/alert";

interface DiscountFormData {
  originalAmount: number;
  discountType: "percent" | "fixed";
  discountValue: number;
  finalAmount: number;
  invoiceDate: string;
  dueDate: string;
}

const StudentFeesDiscount: React.FC = () => {
  const routes = all_routes;
  const { id, invoiceId } = useParams<{ id: string; invoiceId: string }>();
  const navigate = useNavigate();

  // Fetch invoice data
  const {
    data: invoiceResponse,
    isLoading,
    error,
  } = useGetInvoiceByIdQuery(invoiceId || "");

  // Update invoice mutation
  const [
    updateInvoice,
    { isLoading: isUpdating, isSuccess, error: updateError },
  ] = useUpdateInvoiceMutation();

  const [formData, setFormData] = useState<DiscountFormData>({
    originalAmount: 0,
    discountType: "fixed",
    discountValue: 0,
    finalAmount: 0,
    invoiceDate: new Date().toISOString(),
    dueDate: new Date().toISOString(),
  });

  useEffect(() => {
    if (invoiceResponse?.data?.invoice) {
      const invoice = invoiceResponse.data.invoice;
      const amount = parseFloat(invoice.amount);

      // Set initial values from the invoice
      const discountType =
        (invoice.discount_type as "percent" | "fixed") || "fixed";
      const discountValue = invoice.discount_value
        ? parseFloat(invoice.discount_value)
        : 0;

      // Calculate final amount based on existing discount
      let finalAmount = parseFloat(invoice.payable);

      setFormData({
        originalAmount: amount,
        discountType: discountType,
        discountValue: discountValue,
        finalAmount: finalAmount,
        invoiceDate: invoice.invoice_date,
        dueDate: invoice.due_date,
      });
    }
  }, [invoiceResponse]);

  // Effect to handle successful update
  useEffect(() => {
    if (isSuccess) {
      showToast({
        title: "Success",
        text: "Discount applied successfully",
        icon: "success",
      });
      // navigate(routes.studentFees.replace(":id", id || ""));
      navigate(-1)
    }

    if (updateError) {
      showToast({
        title: "Error",
        text: "Failed to apply discount. Please try again.",
        icon: "error",
      });
    }
  }, [isSuccess, updateError, navigate, id, routes.studentFees]);

  const handleBack = () => {
    // navigate(`${routes.studentFees.replace(":id", id || "")}`);
    navigate(-1);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "discountType" || name === "discountValue") {
      const discountType =
        name === "discountType"
          ? (value as "percent" | "fixed")
          : formData.discountType;

      const discountValue =
        name === "discountValue" ? Number(value) : formData.discountValue;

      const originalAmount = formData.originalAmount;

      let finalAmount = originalAmount;

      if (discountType === "percent" && discountValue > 0) {
        finalAmount = originalAmount - originalAmount * (discountValue / 100);
      } else if (discountType === "fixed" && discountValue > 0) {
        finalAmount = originalAmount - discountValue;
      }

      setFormData((prev) => ({
        ...prev,
        [name]:
          name === "discountType"
            ? (value as "percent" | "fixed")
            : Number(value),
        finalAmount: Math.max(0, finalAmount),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Call the update invoice API with the exact payload format
      await updateInvoice({
        id: invoiceId || "",
        updateData: {
          discount_type: formData.discountType,
          discount_value: formData.discountValue,
          amount: formData.originalAmount,
          payable: formData.finalAmount,
          invoice_date: formData.invoiceDate,
          due_date: formData.dueDate,
        },
      }).unwrap();
    } catch (error) {
      console.error("Error applying discount:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content content-two">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading invoice data...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invoiceResponse?.data?.invoice) {
    return (
      <div className="page-wrapper">
        <div className="content content-two">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card">
                <div className="card-body text-center py-5">
                  <div className="text-danger mb-3">
                    <i className="ti ti-alert-circle fa-3x"></i>
                  </div>
                  <h3>Error Loading Invoice</h3>
                  <p>Unable to load the invoice details.</p>
                  <Link
                    to={routes.studentFees.replace(":id", id || "")}
                    className="btn btn-primary mt-3"
                  >
                    Back to Fees
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const invoice = invoiceResponse.data.invoice;

  return (
    <div className="page-wrapper">
      <div className="content content-two">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="mb-1">Apply Discount</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.studentFees.replace(":id", id || "")}>
                    Student Fees
                  </Link>
                </li>
                <li className="breadcrumb-item active">Apply Discount</li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <form onSubmit={handleSubmit}>
              <div className="card">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-discount-2 fs-16" />
                    </span>
                    <h4 className="text-dark">Invoice Details</h4>
                  </div>
                </div>
                <div className="card-body pb-1">
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Invoice ID</label>
                        <input
                          type="text"
                          className="form-control"
                          value={`INV-${invoice.id}`}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Due Date</label>
                        <input
                          type="text"
                          className="form-control"
                          value={new Date(
                            invoice.due_date
                          ).toLocaleDateString()}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Course</label>
                        <input
                          type="text"
                          className="form-control"
                          value={invoice.course.name}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Level</label>
                        <input
                          type="text"
                          className="form-control"
                          value={
                            invoice.type === "registrationfee"
                              ? "Registration"
                              : invoice.stage?.name || "N/A"
                          }
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Original Amount</label>
                        <div className="input-group">
                          <span className="input-group-text">₹</span>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.originalAmount.toFixed(2)}
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Discount Type</label>
                        <select
                          className="form-select"
                          name="discountType"
                          value={formData.discountType}
                          onChange={handleInputChange}
                        >
                          <option value="percent">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (₹)</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Discount Value</label>
                        <div className="input-group">
                          <span className="input-group-text">
                            {formData.discountType === "percent" ? "%" : "₹"}
                          </span>
                          <input
                            type="number"
                            className="form-control"
                            name="discountValue"
                            placeholder="Enter discount value"
                            value={formData.discountValue}
                            onChange={handleInputChange}
                            min="0"
                            max={
                              formData.discountType === "percent"
                                ? "100"
                                : formData.originalAmount.toString()
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Final Amount</label>
                        <div className="input-group">
                          <span className="input-group-text">₹</span>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.finalAmount.toFixed(2)}
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Separate Discount Summary Card */}
              <div className="card mt-4">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-discount-2 fs-16" />
                    </span>
                    <h4 className="text-dark">Discount Summary</h4>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row mb-2">
                    <div className="col-6 text-dark">Original Amount:</div>
                    <div className="col-6 text-end fw-semibold">
                      ₹{formData.originalAmount.toFixed(2)}
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-6 text-dark">Discount:</div>
                    <div className="col-6 text-end text-success fw-semibold">
                      -
                      {formData.discountType === "percent"
                        ? `${formData.discountValue}% (₹${(
                          formData.originalAmount *
                          (formData.discountValue / 100)
                        ).toFixed(2)})`
                        : `₹${Number(formData.discountValue).toFixed(2)}`}
                    </div>
                  </div>
                  <div className="row pt-2 border-top mt-2">
                    <div className="col-6 text-dark fw-bold">Final Amount:</div>
                    <div className="col-6 text-end fw-bold text-primary">
                      ₹{formData.finalAmount.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-end mt-4">
                <button onClick={handleBack} className="btn btn-light me-3">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Applying...
                    </>
                  ) : (
                    "Apply Discount"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentFeesDiscount;
